import { useState } from "react";

import { OneTimeSecret } from "~/components/one-time-secret";
import { Pill } from "~/components/status-badge";
import { TypedConfirmDialog } from "~/components/typed-confirm-dialog";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage, isNotFound } from "~/lib/api";
import { EM_DASH, formatDateTime, formatRelative, orDash } from "~/lib/format";
import {
  useCreateAPIKey,
  useRevokeAPIKey,
  useStorefrontAPIKeys,
  type APIKeyResponse,
} from "~/lib/queries";

/**
 * The API keys tab: mint, list and revoke a storefront's keys.
 *
 * The single biggest gap the old admin had -- onboarding a merchant meant
 * curling the platform-key endpoints, and a compromised key meant a psql
 * update. Everything tier-2 here (only the revoke) goes through the typed
 * confirmation naming the key, because a live merchant integration stops
 * authenticating the instant it commits.
 */
export function APIKeysTab({
  storefrontId,
  storefrontName,
}: {
  storefrontId: string;
  storefrontName: string;
}) {
  const { data, error, isPending, refetch } = useStorefrontAPIKeys(storefrontId);

  if (isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't load API keys</CardTitle>
          <CardDescription>
            {isNotFound(error) ? "This storefront no longer exists." : errorMessage(error)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const active = data.filter((key) => key.active);
  // Revoked and expired are both "not active" but only revocation is this
  // dashboard's action -- an expired-but-unrevoked key still belongs in the
  // active table, since IsActive() already folds expiry in server-side.
  const revoked = data.filter((key) => !key.active && key.revoked_at);
  const other = data.filter((key) => !key.active && !key.revoked_at);

  return (
    <div className="space-y-4">
      <MintCard storefrontId={storefrontId} />

      <KeyTable
        title="Active"
        description="Currently able to authenticate."
        keys={active}
        storefrontId={storefrontId}
        storefrontName={storefrontName}
        showRevoke
      />

      <KeyTable
        title="Revoked"
        description="Kept for audit and search. Revocation is permanent — there is no un-revoke."
        keys={[...revoked, ...other]}
        storefrontId={storefrontId}
        storefrontName={storefrontName}
        showRevoke={false}
      />
    </div>
  );
}

function MintCard({ storefrontId }: { storefrontId: string }) {
  const [name, setName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");
  const [minted, setMinted] = useState<string | null>(null);
  const create = useCreateAPIKey(storefrontId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Mint a new key</CardTitle>
        <CardDescription>
          The token is shown exactly once, immediately after minting. Altura cannot show it
          again — only its prefix and an HMAC of it are ever stored.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate(
              {
                name: name.trim() || undefined,
                expires_in_days: expiresInDays.trim() ? Number(expiresInDays) : undefined,
              },
              {
                onSuccess: (result) => {
                  setMinted(result.key);
                  setName("");
                  setExpiresInDays("");
                },
              },
            );
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="mint-key-name">Name (optional)</Label>
            <Input
              id="mint-key-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. checkout-server"
              className="w-56"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mint-key-expires">Expires in (days, optional)</Label>
            <Input
              id="mint-key-expires"
              type="number"
              min={1}
              value={expiresInDays}
              onChange={(event) => setExpiresInDays(event.target.value)}
              placeholder="Never"
              className="w-40"
            />
          </div>
          <Button type="submit" size="sm" disabled={create.isPending}>
            {create.isPending ? "Minting…" : "Mint key"}
          </Button>
        </form>
        {create.error ? (
          <p className="mt-2 text-xs text-destructive">{errorMessage(create.error)}</p>
        ) : null}
      </CardContent>

      <OneTimeSecret
        open={minted !== null}
        onClose={() => setMinted(null)}
        secret={minted ?? ""}
        label="API key"
        title="API key"
        description="Send this to the merchant's integration. It authenticates every request they make."
        guidance="The live/test environment is encoded in the token's prefix — check it matches what this integration expects before handing it over."
      />
    </Card>
  );
}

function KeyTable({
  title,
  description,
  keys,
  storefrontId,
  storefrontName,
  showRevoke,
}: {
  title: string;
  description: string;
  keys: APIKeyResponse[];
  storefrontId: string;
  storefrontName: string;
  showRevoke: boolean;
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {keys.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">Nothing here yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-6">Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>
                  Last used
                  <span className="ml-1 font-normal text-muted-foreground/70">(approx.)</span>
                </TableHead>
                <TableHead>{showRevoke ? "Expires" : "Revoked"}</TableHead>
                <TableHead className="pr-6" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((key) => (
                <KeyRow
                  key={key.id}
                  apiKey={key}
                  storefrontId={storefrontId}
                  storefrontName={storefrontName}
                  showRevoke={showRevoke}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function KeyRow({
  apiKey,
  storefrontId,
  storefrontName,
  showRevoke,
}: {
  apiKey: APIKeyResponse;
  storefrontId: string;
  storefrontName: string;
  showRevoke: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const revoke = useRevokeAPIKey(storefrontId);

  const displayName = apiKey.name ?? apiKey.key_prefix;

  return (
    <TableRow>
      <TableCell className="pl-6">{orDash(apiKey.name)}</TableCell>
      <TableCell className="font-mono text-[0.8125rem]">{apiKey.key_prefix}</TableCell>
      <TableCell className="text-muted-foreground">{formatDateTime(apiKey.created_at)}</TableCell>
      <TableCell
        className="text-muted-foreground"
        title={apiKey.last_used_at ? formatDateTime(apiKey.last_used_at) : undefined}
      >
        {apiKey.last_used_at ? formatRelative(apiKey.last_used_at) : "never"}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {showRevoke ? (
          apiKey.expires_at ? (
            formatDateTime(apiKey.expires_at)
          ) : (
            EM_DASH
          )
        ) : (
          <span title={apiKey.revoked_reason ?? undefined}>
            {apiKey.revoked_at ? formatDateTime(apiKey.revoked_at) : EM_DASH}
            {apiKey.revoked_reason ? ` — ${apiKey.revoked_reason}` : ""}
          </span>
        )}
      </TableCell>
      <TableCell className="pr-6 text-right">
        {showRevoke ? (
          <>
            <Button
              variant="destructive"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => setConfirming(true)}
            >
              Revoke
            </Button>
            <TypedConfirmDialog
              open={confirming}
              onOpenChange={(next) => {
                setConfirming(next);
                if (!next) setReason("");
              }}
              title={`Revoke the key "${displayName}"?`}
              targetLabel="key name"
              expectedValue={displayName}
              confirmLabel={revoke.isPending ? "Revoking…" : "Revoke key"}
              pending={revoke.isPending}
              confirmDisabled={!reason.trim()}
              consequence={`${storefrontName}'s integration will stop authenticating with this key immediately. Any request still using it will get 401s with no warning.`}
              onConfirm={() => {
                revoke.mutate(
                  { keyId: apiKey.id, reason: reason.trim() || undefined },
                  { onSuccess: () => setConfirming(false) },
                );
              }}
              body={
                <>
                  <p>
                    Last used:{" "}
                    <strong className="font-medium text-foreground">
                      {apiKey.last_used_at ? formatRelative(apiKey.last_used_at) : "never"}
                    </strong>
                    {apiKey.last_used_at ? (
                      <span className="text-muted-foreground"> ({formatDateTime(apiKey.last_used_at)})</span>
                    ) : null}
                  </p>
                  <div className="space-y-1.5">
                    <Label htmlFor="revoke-reason">Reason for revoking</Label>
                    <Input
                      id="revoke-reason"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="e.g. compromised, rotated by merchant request"
                    />
                  </div>
                  {revoke.error ? (
                    <p className="text-destructive">{errorMessage(revoke.error)}</p>
                  ) : null}
                </>
              }
            />
          </>
        ) : (
          <Pill tone="neutral">revoked</Pill>
        )}
      </TableCell>
    </TableRow>
  );
}
